import React, { createContext, useContext, useEffect, useReducer, useCallback, useRef, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';

// Tipos para eventos WebSocket
export interface MetricsUpdate {
  totalConversations: number;
  activeConversations: number;
  queuedConversations: number;
  averageResponseTime: number;
  agentPerformance: Array<{
    agentId: string;
    name: string;
    activeChats: number;
    averageResponseTime: number;
    satisfaction: number;
  }>;
  timestamp: string;
}

export interface ConversationUpdate {
  id: string;
  phoneNumber: string;
  customerName?: string;
  status: 'ACTIVE' | 'QUEUED' | 'CLOSED';
  assignedAgent?: string;
  lastMessage?: {
    content: string;
    timestamp: string;
    direction: 'INBOUND' | 'OUTBOUND';
  };
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  tags?: string[];
  queuePosition?: number;
  waitTime?: number;
  // Flag opcional para identificar conversaciones completamente nuevas
  isNew?: boolean;
  // Propiedades de control de conversación
  control?: 'BOT' | 'HUMAN' | 'UNASSIGNED';
  controlType?: string;
  controlledBy?: string | null;
  controlledByUsername?: string | null;
  takeoverNotes?: string | null;
}

export interface AgentStatusUpdate {
  agentId: string;
  name: string;
  status: 'ONLINE' | 'OFFLINE' | 'BUSY' | 'AWAY';
  activeChats: number;
  lastActivity: string;
}

export interface SystemAlert {
  id: string;
  type: 'INFO' | 'WARNING' | 'ERROR' | 'SUCCESS';
  title: string;
  message: string;
  timestamp: string;
  autoHide?: boolean;
}

export interface WindowAlert {
  id: string;
  type: 'EARLY' | 'URGENT' | 'CRITICAL' | 'EXPIRED';
  level: 'EARLY' | 'URGENT' | 'CRITICAL' | 'EXPIRED';
  chatId: string;
  agentId?: string;
  customerName: string;
  phoneNumber: string;
  timeRemaining: string;
  message: string;
  suggestedActions: string[];
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  timestamp: string;
  acknowledged?: boolean;
  acknowledgedBy?: string;
  data?: {
    timeElapsed: string;
    expiresAt: string;
    percentageUsed: number;
    isExpired: boolean;
    requiresHSM?: boolean;
  };
}

export interface WindowTimeUpdate {
  chatId: string;
  customerName: string;
  phoneNumber: string;
  timeRemaining: string;
  percentageUsed: number;
  alertLevel: 'SAFE' | 'EARLY' | 'URGENT' | 'CRITICAL' | 'EXPIRED';
  expiresAt: string;
}

export interface WindowStatistics {
  totalActiveChats: number;
  chatsNearExpiry: number;
  expiredChats: number;
  alertsSentToday: number;
  averageResponseTime: number;
  chatsByTimeRemaining: {
    safe: number;
    early: number;
    urgent: number;
    critical: number;
    expired: number;
  };
}

// Estado del WebSocket
export interface WebSocketState {
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  metrics: MetricsUpdate | null;
  conversations: ConversationUpdate[];
  agents: AgentStatusUpdate[];
  alerts: SystemAlert[];
  windowAlerts: WindowAlert[];
  windowStatistics: WindowStatistics | null;
  windowTimeUpdates: WindowTimeUpdate[];
  connectionId: string | null;
}

// Acciones del reducer
type WebSocketAction =
  | { type: 'CONNECTING' }
  | { type: 'CONNECTED'; payload: string }
  | { type: 'DISCONNECTED' }
  | { type: 'ERROR'; payload: string }
  | { type: 'METRICS_UPDATE'; payload: MetricsUpdate }
  | { type: 'CONVERSATION_UPDATE'; payload: ConversationUpdate }
  | { type: 'CONVERSATIONS_BATCH_UPDATE'; payload: ConversationUpdate[] }
  | { type: 'CONVERSATIONS_LIST'; payload: ConversationUpdate[] }
  | { type: 'AGENT_STATUS_UPDATE'; payload: AgentStatusUpdate }
  | { type: 'AGENTS_LIST'; payload: AgentStatusUpdate[] }
  | { type: 'SYSTEM_ALERT'; payload: SystemAlert }
  | { type: 'REMOVE_ALERT'; payload: string }
  | { type: 'CLEAR_ALERTS' }
  | { type: 'WINDOW_ALERT'; payload: WindowAlert }
  | { type: 'WINDOW_STATISTICS'; payload: WindowStatistics }
  | { type: 'WINDOW_TIME_UPDATES'; payload: WindowTimeUpdate[] }
  | { type: 'WINDOW_CHAT_EXPIRED'; payload: { chatId: string; info: any } }
  | { type: 'WINDOW_RENEWED'; payload: { chatId: string; info: any } }
  | { type: 'ACKNOWLEDGE_WINDOW_ALERT'; payload: { alertId: string; userId: string } }
  | { type: 'REMOVE_WINDOW_ALERT'; payload: string }
  | { type: 'CONVERSATION_CONTROL_CHANGED'; payload: any }
  | { type: 'CONVERSATION_RELEASE'; payload: any };

// Estado inicial
const initialState: WebSocketState = {
  isConnected: false,
  isConnecting: false,
  error: null,
  metrics: null,
  conversations: [],
  agents: [],
  alerts: [],
  windowAlerts: [],
  windowStatistics: null,
  windowTimeUpdates: [],
  connectionId: null,
};

// Reducer
const webSocketReducer = (state: WebSocketState, action: WebSocketAction): WebSocketState => {
  switch (action.type) {
    case 'CONNECTING':
      return {
        ...state,
        isConnecting: true,
        error: null,
      };

    case 'CONNECTED':
      return {
        ...state,
        isConnected: true,
        isConnecting: false,
        error: null,
        connectionId: action.payload as string,
      };

    case 'DISCONNECTED':
      return {
        ...state,
        isConnected: false,
        isConnecting: false,
        connectionId: null,
      };

    case 'ERROR':
      return {
        ...state,
        isConnected: false,
        isConnecting: false,
        error: action.payload,
      };

    case 'METRICS_UPDATE':
      return {
        ...state,
        metrics: action.payload,
      };

    case 'CONVERSATION_UPDATE': {
      // Protección contra payloads incompletos
      if (!action.payload || !action.payload.id) {
        console.warn('CONVERSATION_UPDATE ignorado por falta de id:', action.payload);
        return state;
      }
      const updatedConversations = state.conversations.map(conv => {
        if (conv.id === action.payload.id) {
          const updated: any = { ...conv };

          // Copy only defined properties to avoid exactOptionalPropertyTypes issues
          Object.keys(action.payload).forEach(key => {
            const k = key as keyof ConversationUpdate;
            if (action.payload[k] !== undefined) {
              updated[k] = action.payload[k];
            }
          });

          return updated as ConversationUpdate;
        }
        return conv;
      });

      // Si no existe, agregarlo
      if (!updatedConversations.find(conv => conv.id === action.payload.id)) {
        updatedConversations.push(action.payload);
      }

      return {
        ...state,
        conversations: updatedConversations,
      };
    }

    case 'CONVERSATIONS_BATCH_UPDATE': {
      // Aplica un lote de updates en una sola transición de estado.
      // Evita N re-renders cuando llegan ráfagas de mensajes (ej: 10 chats activos a la vez).
      const updates = action.payload;
      if (!Array.isArray(updates) || updates.length === 0) return state;

      // Map mantiene el orden de inserción → conversaciones existentes preservan su posición,
      // las nuevas se agregan al final. Mismo comportamiento que CONVERSATION_UPDATE individual.
      const byId = new Map<string, ConversationUpdate>();
      state.conversations.forEach(c => byId.set(c.id, c));

      let mutated = false;
      for (const payload of updates) {
        if (!payload || !payload.id) continue;
        const existing = byId.get(payload.id);
        if (existing) {
          const merged: any = { ...existing };
          Object.keys(payload).forEach(key => {
            const k = key as keyof ConversationUpdate;
            if (payload[k] !== undefined) merged[k] = payload[k];
          });
          byId.set(payload.id, merged as ConversationUpdate);
          mutated = true;
        } else {
          byId.set(payload.id, payload);
          mutated = true;
        }
      }

      if (!mutated) return state;
      return {
        ...state,
        conversations: Array.from(byId.values()),
      };
    }

    case 'CONVERSATION_CONTROL_CHANGED': {
      const chatIdStr = String(action.payload?.chatId);
      return {
        ...state,
        conversations: state.conversations.map(conv => {
          if (conv.id === chatIdStr) {
            const controller = action.payload?.controlledBy;
            const fullName = controller?.fullName || controller?.username || controller || '';
            const username = controller?.username || '';

            return {
              ...conv,
              control: action.payload?.control || conv.control || 'HUMAN',
              controlType: action.payload?.eventType || conv.controlType || 'TAKEOVER',
              controlledBy: fullName,
              controlledByUsername: username,
              takeoverNotes: action.payload?.notes || (conv as any).takeoverNotes,
              assignedAgent: fullName || conv.assignedAgent,
            } as any;
          }
          return conv;
        }),
      };
    }

    case 'CONVERSATION_RELEASE': {
      const releaseChatIdStr = String(action.payload?.chatId);
      return {
        ...state,
        conversations: state.conversations.map(conv => {
          if (conv.id === releaseChatIdStr) {
            return {
              ...conv,
              control: 'BOT',
              controlType: 'RELEASED',
              controlledBy: null,
              controlledByUsername: null,
              takeoverNotes: null,
            } as any;
          }
          return conv;
        }),
      };
    }

    case 'CONVERSATIONS_LIST':
      return {
        ...state,
        conversations: action.payload,
      };

    case 'AGENT_STATUS_UPDATE':
      const updatedAgents = state.agents.map(agent =>
        agent.agentId === action.payload.agentId ? action.payload : agent
      );

      // Si no existe, agregarlo
      if (!updatedAgents.find(agent => agent.agentId === action.payload.agentId)) {
        updatedAgents.push(action.payload);
      }

      return {
        ...state,
        agents: updatedAgents,
      };

    case 'AGENTS_LIST':
      return {
        ...state,
        agents: action.payload,
      };

    case 'SYSTEM_ALERT':
      return {
        ...state,
        alerts: [action.payload, ...state.alerts].slice(0, 10), // Mantener solo 10 alertas
      };

    case 'REMOVE_ALERT':
      return {
        ...state,
        alerts: state.alerts.filter(alert => alert.id !== action.payload),
      };

    case 'CLEAR_ALERTS':
      return {
        ...state,
        alerts: [],
      };

    case 'WINDOW_ALERT':
      return {
        ...state,
        windowAlerts: [action.payload, ...state.windowAlerts].slice(0, 20), // Mantener solo 20 alertas
      };

    case 'WINDOW_STATISTICS':
      return {
        ...state,
        windowStatistics: action.payload,
      };

    case 'WINDOW_TIME_UPDATES':
      return {
        ...state,
        windowTimeUpdates: action.payload,
      };

    case 'WINDOW_CHAT_EXPIRED':
      // Actualizar la conversación como expirada
      const expiredConversations = state.conversations.map(conv =>
        conv.id === action.payload.chatId
          ? { ...conv, status: 'EXPIRED_24H' as any }
          : conv
      );

      return {
        ...state,
        conversations: expiredConversations,
      };

    case 'WINDOW_RENEWED':
      // Actualizar la conversación como activa
      const renewedConversations = state.conversations.map(conv =>
        conv.id === action.payload.chatId
          ? { ...conv, status: 'ACTIVE' as any }
          : conv
      );

      return {
        ...state,
        conversations: renewedConversations,
      };

    case 'ACKNOWLEDGE_WINDOW_ALERT':
      return {
        ...state,
        windowAlerts: state.windowAlerts.map(alert =>
          alert.id === action.payload.alertId
            ? { ...alert, acknowledged: true, acknowledgedBy: action.payload.userId }
            : alert
        ),
      };

    case 'REMOVE_WINDOW_ALERT':
      return {
        ...state,
        windowAlerts: state.windowAlerts.filter(alert => alert.id !== action.payload),
      };

    default:
      return state;
  }
};

// Contexto del WebSocket
interface WebSocketContextType extends WebSocketState {
  reconnect: () => void;
  disconnect: () => void;
  emit: (event: string, data: any) => void;
  removeAlert: (id: string) => void;
  clearAllAlerts: () => void;
  acknowledgeWindowAlert: (alertId: string) => void;
  monitorConversation: (conversationId: string) => void;
  stopMonitoringConversation: (conversationId: string) => void;
  subscribeToConversationMessages: (conversationId: string, listener: (payload: any) => void) => void;
  unsubscribeFromConversationMessages: (conversationId: string, listener: (payload: any) => void) => void;
  subscribeToConversations: (filters?: { status?: string; agent?: string; priority?: string; departmentId?: number }) => void;
  unsubscribeFromConversations: () => void;
  getWindowStatistics: () => void;
  subscribeToWindowAlerts: (level: string) => void;
  unsubscribeFromWindowAlerts: (level: string) => void;
  notifications: SystemAlert[];
  markNotificationAsRead: (id: string) => void;
  subscribeToMetrics: () => void;
  removeWindowAlert: (alertId: string) => void;
  joinDashboard: () => void;
  setCurrentlyViewingConversation: (id: string | null) => void;
  markConversationAsRead: (id: string) => void;
  subscribeToUnreadChanges: (listener: () => void) => void;
  unsubscribeFromUnreadChanges: (listener: () => void) => void;
  getUnreadCount: (id: string) => number;
  subscribeToControlChanges: (listener: (payload: any) => void) => void;
  unsubscribeFromControlChanges: (listener: (payload: any) => void) => void;
  // Listener para "el WS reconectó después de una caída real" — la página puede usarlo
  // para refrescar su estado (lista de conversaciones, mensajes, etc.) y recuperar eventos
  // perdidos durante el corte. NO se dispara en la primera conexión, solo en reconexiones.
  subscribeToReconnect: (listener: () => void) => () => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

// Proveedor del contexto
export const WebSocketProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(webSocketReducer, initialState);
  const { user, token } = useAuth();
  const socketRef = useRef<Socket | null>(null);
  const reconnectTimer = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const messageListenersRef = useRef<Map<string, Set<(payload: any) => void>>>(new Map());
  const unreadCountsRef = useRef<Map<string, number>>(new Map());
  const currentlyViewingRef = useRef<string | null>(null);
  const unreadListenersRef = useRef<Set<() => void>>(new Set());
  const controlChangeListenersRef = useRef<Set<(payload: any) => void>>(new Set());
  const reconnectListenersRef = useRef<Set<() => void>>(new Set());
  const storageKeyRef = useRef<string | null>(null);

  // Batching de eventos conversation:update — agrupa ráfagas en un solo dispatch por frame.
  // Sin esto, 10 mensajes en 200ms = 10 re-renders de la lista; con esto = 1 re-render.
  const conversationBatchRef = useRef<ConversationUpdate[]>([]);
  const flushScheduledRef = useRef<number | null>(null);

  // Page Visibility: cuando la pestaña está oculta (operador en otra ventana, PC bloqueada),
  // los eventos del WS siguen llegando pero NO se dispatchan al reducer — solo se acumulan.
  // Al volver la visibilidad, se flushea todo lo acumulado en un solo batch.
  // Esto evita que los `'message' handler took 200-400ms` bloqueen el main thread en idle.
  const isPageVisibleRef = useRef<boolean>(typeof document !== 'undefined' ? !document.hidden : true);

  // Tope de seguridad: si el buffer crece más de esto mientras está oculto (ej: pestaña
  // dormida varios minutos en una sala con mucha actividad), forzar flush para no acumular
  // RAM ilimitada. 200 actualizaciones es bastante margen para un uso normal.
  const MAX_BUFFERED_UPDATES = 200;

  const flushConversationBatch = () => {
    flushScheduledRef.current = null;
    const batch = conversationBatchRef.current;
    if (batch.length === 0) return;
    conversationBatchRef.current = [];
    const first = batch[0];
    if (batch.length === 1 && first) {
      dispatch({ type: 'CONVERSATION_UPDATE', payload: first });
    } else {
      dispatch({ type: 'CONVERSATIONS_BATCH_UPDATE', payload: batch });
    }
  };
  const scheduleConversationFlush = () => {
    if (flushScheduledRef.current !== null) return;
    // Si la pestaña está oculta, NO programar flush — los eventos siguen acumulándose
    // en el buffer y se flushearán cuando vuelva la visibilidad (o cuando se llene el tope).
    if (!isPageVisibleRef.current) return;
    if (typeof requestAnimationFrame !== 'undefined') {
      flushScheduledRef.current = requestAnimationFrame(flushConversationBatch);
    } else {
      // Fallback en entornos sin rAF (tests). 16ms ≈ 1 frame a 60fps.
      flushScheduledRef.current = (setTimeout(flushConversationBatch, 16) as unknown) as number;
    }
  };

  const RECONNECT_INTERVAL = 5000;
  const MAX_RECONNECT_DELAY = 30000;

  const saveUnreadToStorage = () => {
    if (!storageKeyRef.current) return;
    try {
      const obj: Record<string, number> = {};
      unreadCountsRef.current.forEach((count, id) => { obj[id] = count; });
      localStorage.setItem(storageKeyRef.current, JSON.stringify(obj));
    } catch (_) {}
  };

  const playUnreadBeep = () => {
    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) return;
      const ctx = new AudioContextClass();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.frequency.value = 880;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.3);
    } catch (_) {}
  };

  const notifyUnreadListeners = () => {
    unreadListenersRef.current.forEach(listener => {
      try { listener(); } catch (_) {}
    });
  };

  const updateTitleWithUnread = () => {
    const total = Array.from(unreadCountsRef.current.values()).reduce((a, b) => a + b, 0);
    document.title = total > 0 ? `(${total}) CRM WhatsApp` : 'CRM WhatsApp';
  };

  const connect = useCallback((): (() => void) | void => {
    if (!token || (socketRef.current && socketRef.current.connected)) {
      return;
    }

    dispatch({ type: 'CONNECTING' });

    const wsUrl = (import.meta as any).env?.VITE_WS_URL || window.location.origin;
    const newSocket = io(wsUrl, {
      path: '/socket.io',
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: false,
    });

    socketRef.current = newSocket;

    newSocket.on('connect', () => {
      console.log('WebSocket conectado con ID:', newSocket.id);
      const wasReconnection = reconnectAttempts.current > 0;
      dispatch({ type: 'CONNECTED', payload: newSocket.id as string });
      reconnectAttempts.current = 0;
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
      // Si fue una reconexión real (no la primera conexión), avisar a los
      // suscriptores para que refresquen su estado y recuperen mensajes
      // que pudieron haber llegado durante el corte.
      if (wasReconnection && reconnectListenersRef.current.size > 0) {
        console.log(`📡 WS reconectado — disparando refetch a ${reconnectListenersRef.current.size} suscriptor(es)`);
        reconnectListenersRef.current.forEach(cb => {
          try { cb(); } catch (e) { console.error('Error en listener de reconexión:', e); }
        });
      }
    });

    newSocket.on('disconnect', (reason: string) => {
      console.warn('WebSocket desconectado:', reason);
      dispatch({ type: 'DISCONNECTED' });
      if (reason !== 'io client disconnect') {
        // Solo intentar reconectar si no fue una desconexión manual
        handleReconnect();
      }
    });

    newSocket.on('connect_error', (error: Error) => {
      console.error('Error de conexión WebSocket:', error.message);
      dispatch({ type: 'ERROR', payload: error.message });
      handleReconnect();
    });

    // Eventos emitidos por el servidor (nombres con ':')
    newSocket.on('metrics:update', (data: MetricsUpdate) => {
      // Métricas son de baja prioridad — si la pestaña no está visible, no vale la pena
      // dispatchar (nadie está mirando el dashboard). El operador verá métricas actualizadas
      // la próxima vez que vuelva (el server las re-emite cada N segundos).
      if (!isPageVisibleRef.current) return;
      dispatch({ type: 'METRICS_UPDATE', payload: data });
    });

    newSocket.on('conversation:update', (data: any) => {
      try {
        const payload: ConversationUpdate = data?.update
          ? { id: data.update.id || String(data.chatId), ...data.update }
          : data;
        if (!payload?.id) {
          console.warn('Evento conversation:update sin id válido:', data);
          return;
        }
        // Encolar y flushear en el próximo frame (max 1 dispatch por frame).
        // Si la pestaña está oculta, scheduleConversationFlush() no programa nada y el buffer
        // sigue creciendo hasta que la pestaña vuelva o se alcance MAX_BUFFERED_UPDATES.
        conversationBatchRef.current.push(payload);
        if (conversationBatchRef.current.length >= MAX_BUFFERED_UPDATES) {
          // Tope alcanzado — forzar flush aunque la pestaña esté oculta (evita memory leak)
          flushConversationBatch();
        } else {
          scheduleConversationFlush();
        }
      } catch (err) {
        console.error('Error procesando conversation:update:', err);
      }
    });

    const normalizeConv = (item: any): ConversationUpdate => {
      const lm: any = item?.lastMessage;
      const dir = (lm?.direction || lm?.dir || '').toString().toUpperCase();
      const normalizedDir = dir === 'IN' || dir === 'INBOUND' ? 'INBOUND' : (dir === 'OUT' || dir === 'OUTBOUND' ? 'OUTBOUND' : 'INBOUND');
      return {
        id: String(item?.id || item?.chatId || ''),
        phoneNumber: item?.phoneNumber || item?.customerPhone || '',
        customerName: item?.customerName || item?.name || item?.phoneNumber || '',
        status: (item?.status || 'ACTIVE').toString().toUpperCase() as any,
        assignedAgent: item?.assignedAgent || item?.agentId || item?.agentName || '',
        ...(lm ? {
          lastMessage: {
            content: lm?.content ?? lm?.body ?? '',
            timestamp: lm?.timestamp ?? new Date().toISOString(),
            direction: normalizedDir as any,
          }
        } : {}),
        priority: (item?.priority || 'MEDIUM').toString().toUpperCase() as any,
        queuePosition: item?.queuePosition,
        waitTime: item?.waitTime,
        isNew: item?.isNew === true,
      } as ConversationUpdate;
    };

    const handleConversationsList = (data: any) => {
      try {
        const list: ConversationUpdate[] = Array.isArray(data?.conversations)
          ? (data.conversations as any[]).map(normalizeConv)
          : (Array.isArray(data) ? (data as any[]).map(normalizeConv) : []);
        if (list.length > 0) {
          dispatch({ type: 'CONVERSATIONS_LIST', payload: list });
        }
      } catch (err) {
        console.error('Error procesando lista de conversaciones:', err);
      }
    };

    newSocket.on('conversations:list', handleConversationsList);
    newSocket.on('dashboard:conversations', handleConversationsList);
    newSocket.on('dashboard:conversations_list', handleConversationsList);

    newSocket.on('agent:status_change', (data: AgentStatusUpdate) => {
      dispatch({ type: 'AGENT_STATUS_UPDATE', payload: data });
    });

    newSocket.on('system:alert', (data: SystemAlert) => {
      dispatch({ type: 'SYSTEM_ALERT', payload: data });
    });

    newSocket.on('window:alert', (data: WindowAlert) => {
      dispatch({ type: 'WINDOW_ALERT', payload: data });
    });

    newSocket.on('window:statistics_response', (data: WindowStatistics) => {
      dispatch({ type: 'WINDOW_STATISTICS', payload: data });
    });

    // Evento real emitido por timerService — mapear campos del backend al formato del frontend
    newSocket.on('window:statistics', (data: any) => {
      const mapped: WindowStatistics = {
        totalActiveChats: data.totalActiveChats || 0,
        chatsNearExpiry: (data.early || 0) + (data.urgent || 0),
        expiredChats: data.totalExpiredChats || data.expired || 0,
        alertsSentToday: data.alertsSentToday || 0,
        averageResponseTime: data.averageTimeUsed || 0,
        chatsByTimeRemaining: {
          safe: data.normal || data.safe || 0,
          early: data.early || 0,
          urgent: data.urgent || 0,
          critical: data.critical || 0,
          expired: data.expired || 0,
        },
      };
      dispatch({ type: 'WINDOW_STATISTICS', payload: mapped });
    });

    newSocket.on('window:renewed', (data: { chatId: string; info: any }) => {
      dispatch({ type: 'WINDOW_RENEWED', payload: data });
    });

    newSocket.on('conversation:control_changed', (payload: any) => {
      console.log('Control de conversación cambiado:', payload);
      dispatch({ type: 'CONVERSATION_CONTROL_CHANGED', payload });
      controlChangeListenersRef.current.forEach(listener => {
        try { listener(payload); } catch (_) {}
      });
    });

    newSocket.on('conversation:release', (payload: any) => {
      console.log('Conversación liberada:', payload);
      dispatch({ type: 'CONVERSATION_RELEASE', payload });
      controlChangeListenersRef.current.forEach(listener => {
        try { listener(payload); } catch (_) {}
      });
    });

    // Compatibilidad con eventos adicionales del backend
    newSocket.on('conversation:new_message', (payload: any) => {
      try {
        const chatId = (payload?.chatId || payload?.conversationId || payload?.message?.chatId || payload?.id || '').toString();
        if (!chatId) {
          console.warn('WS nuevo mensaje sin chatId identificable:', payload);
          return;
        }

        // Rastrear no leídos si el mensaje es entrante y no es la conversación activa
        if (!payload?._isStatusUpdate) {
          const direction = (payload?.direction || payload?.message?.direction || '').toString().toUpperCase();
          const isInbound = direction === 'INBOUND' || direction === 'IN';
          if (isInbound && chatId !== currentlyViewingRef.current) {
            const current = unreadCountsRef.current.get(chatId) || 0;
            unreadCountsRef.current.set(chatId, current + 1);
            saveUnreadToStorage();
            playUnreadBeep();
            updateTitleWithUnread();
            notifyUnreadListeners();
          }
        }

        const listeners = messageListenersRef.current.get(chatId);
        if (listeners && listeners.size > 0) {
          listeners.forEach(listener => {
            try { listener(payload); } catch (err) { console.error('Error en listener de mensaje:', err); }
          });
        }
      } catch (err) {
        console.error('Error manejando conversation:new_message:', err);
      }
    });

    // Evento para actualización de status de mensajes (palomitas: sent/delivered/read)
    newSocket.on('message:status_update', (payload: any) => {
      try {
        const chatId = (payload?.chatId || '').toString();
        if (!chatId) {
          console.warn('WS status update sin chatId identificable:', payload);
          return;
        }
        // Reenviar a los listeners de la conversación específica
        const listeners = messageListenersRef.current.get(chatId);
        if (listeners && listeners.size > 0) {
          listeners.forEach(listener => {
            try {
              // Pasar el payload con un flag especial para identificar que es un status update
              listener({ ...payload, _isStatusUpdate: true });
            } catch (err) {
              console.error('Error en listener de status update:', err);
            }
          });
        }
        console.log('📩 Status update recibido:', payload.messageWaId, '->', payload.status);
      } catch (err) {
        console.error('Error manejando message:status_update:', err);
      }
    });

    return () => {
      if (newSocket) {
        console.log('Limpiando y desconectando socket...');
        newSocket.disconnect();
        socketRef.current = null;
      }
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
      }
      // Cancelar flush pendiente de conversations (si quedó alguno encolado al desconectarse)
      if (flushScheduledRef.current !== null) {
        if (typeof cancelAnimationFrame !== 'undefined') {
          cancelAnimationFrame(flushScheduledRef.current);
        } else {
          clearTimeout(flushScheduledRef.current as unknown as ReturnType<typeof setTimeout>);
        }
        flushScheduledRef.current = null;
      }
      conversationBatchRef.current = [];
    };
  }, [token]);

  const handleReconnect = () => {
    if (reconnectTimer.current) return; // Ya hay un intento en curso

    reconnectAttempts.current += 1;
    const delay = Math.min(RECONNECT_INTERVAL * reconnectAttempts.current, MAX_RECONNECT_DELAY);
    console.log(`Intento de reconexión ${reconnectAttempts.current} en ${delay / 1000}s...`);

    reconnectTimer.current = setTimeout(() => {
      reconnectTimer.current = null;
      connect();
    }, delay);
  };

  // Page Visibility API: detectar cuando la pestaña se oculta/muestra.
  // Al volver a visible, flushear inmediatamente cualquier batch acumulado.
  // Esto se monta UNA SOLA VEZ (sin deps) — no depende del usuario.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const handleVisibility = () => {
      const nowVisible = !document.hidden;
      const wasHidden = !isPageVisibleRef.current;
      isPageVisibleRef.current = nowVisible;
      if (nowVisible && wasHidden) {
        // Volvió a primer plano — flushear lo que se acumuló mientras estaba oculta
        if (conversationBatchRef.current.length > 0) {
          console.log(`📺 Pestaña visible — flush de ${conversationBatchRef.current.length} updates acumuladas`);
          flushConversationBatch();
        }
      } else if (!nowVisible) {
        console.log('📺 Pestaña oculta — pausando dispatches del WS (eventos se acumulan en buffer)');
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Cargar no-leídos desde localStorage cuando cambia el usuario
  useEffect(() => {
    if (user?.id) {
      const key = `crm_unread_${user.id}`;
      storageKeyRef.current = key;
      try {
        const saved = localStorage.getItem(key);
        if (saved) {
          const obj = JSON.parse(saved) as Record<string, number>;
          const map = new Map<string, number>();
          Object.entries(obj).forEach(([id, count]) => {
            if (typeof count === 'number' && count > 0) map.set(id, count);
          });
          unreadCountsRef.current = map;
          updateTitleWithUnread();
          notifyUnreadListeners();
        }
      } catch (_) {}
    } else {
      storageKeyRef.current = null;
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user && token) {
      const cleanup = connect();
      return cleanup;
    } else {
      // Logout o pérdida de auth: desconectar socket y limpiar todo
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
      reconnectAttempts.current = 0;
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      // Limpiar contadores de no-leídos y título
      unreadCountsRef.current.clear();
      document.title = 'CRM WhatsApp';
      notifyUnreadListeners();
      dispatch({ type: 'DISCONNECTED' });
    }
  }, [user, token, connect]);

  const reconnect = () => {
    if (socketRef.current && !socketRef.current.connected) {
      reconnectAttempts.current = 0;
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
      connect();
    }
  };

  const disconnect = () => {
    if (socketRef.current) {
      socketRef.current.disconnect();
    }
  };

  const emit = (event: string, data: any) => {
    if (socketRef.current && socketRef.current.connected) {
      socketRef.current.emit(event, data);
    } else {
      console.warn(`Socket no conectado. No se pudo emitir el evento '${event}'.`);
    }
  };

  const removeAlert = (id: string) => {
    dispatch({ type: 'REMOVE_ALERT', payload: id });
  };

  const clearAllAlerts = () => {
    dispatch({ type: 'CLEAR_ALERTS' });
  };

  const acknowledgeWindowAlert = (alertId: string) => {
    if (user) {
      dispatch({ type: 'ACKNOWLEDGE_WINDOW_ALERT', payload: { alertId, userId: user.id } });
      emit('window:acknowledge', { alertId, userId: user.id });
    }
  };

  const monitorConversation = (conversationId: string) => {
    emit('conversation:monitor', { chatId: conversationId });
  };

  const stopMonitoringConversation = (conversationId: string) => {
    emit('conversation:unmonitor', { chatId: conversationId });
  };

  const subscribeToConversationMessages = (conversationId: string, listener: (payload: any) => void) => {
    if (!messageListenersRef.current.has(conversationId)) {
      messageListenersRef.current.set(conversationId, new Set());
    }
    messageListenersRef.current.get(conversationId)!.add(listener);
  };

  const unsubscribeFromConversationMessages = (conversationId: string, listener: (payload: any) => void) => {
    const set = messageListenersRef.current.get(conversationId);
    if (set) {
      set.delete(listener);
      if (set.size === 0) {
        messageListenersRef.current.delete(conversationId);
      }
    }
  };

  const subscribeToConversations = (filters?: { status?: string; agent?: string; priority?: string; departmentId?: number }) => {
    const payload = {
      ...(filters?.status ? { status: filters.status } : {}),
      ...(filters?.agent ? { agent: filters.agent } : {}),
      ...(filters?.priority ? { priority: filters.priority } : {}),
      ...(typeof filters?.departmentId === 'number' ? { departmentId: filters.departmentId } : {}),
    };
    emit('conversations:subscribe', payload);
    emit('dashboard:subscribe_conversations', payload);
  };

  const unsubscribeFromConversations = () => {
    emit('conversations:unsubscribe', {});
    emit('dashboard:unsubscribe_conversations', {});
  };

  const getWindowStatistics = () => {
    emit('window:get_statistics', {});
  };

  const subscribeToWindowAlerts = (level: string) => {
    emit('window:subscribe', { level });
  };

  const unsubscribeFromWindowAlerts = (level: string) => {
    emit('window:unsubscribe', { level });
  };

  const markNotificationAsRead = (id: string) => {
    dispatch({ type: 'REMOVE_ALERT', payload: id });
  };

  const subscribeToMetrics = () => {
    emit('metrics:subscribe', {});
  };

  const removeWindowAlert = (alertId: string) => {
    dispatch({ type: 'REMOVE_WINDOW_ALERT', payload: alertId });
  };

  const joinDashboard = () => {
    emit('dashboard:join', {
      userId: user?.id,
      username: (user as any)?.username || user?.email || 'usuario',
      role: (user as any)?.role || 'user',
    });
  };

  const setCurrentlyViewingConversation = (id: string | null) => {
    currentlyViewingRef.current = id;
    if (id) {
      unreadCountsRef.current.delete(id);
      saveUnreadToStorage();
      updateTitleWithUnread();
      notifyUnreadListeners();
    }
  };

  const markConversationAsRead = (id: string) => {
    unreadCountsRef.current.delete(id);
    saveUnreadToStorage();
    updateTitleWithUnread();
    notifyUnreadListeners();
  };

  const subscribeToUnreadChanges = (listener: () => void) => {
    unreadListenersRef.current.add(listener);
  };

  const unsubscribeFromUnreadChanges = (listener: () => void) => {
    unreadListenersRef.current.delete(listener);
  };

  const subscribeToControlChanges = (listener: (payload: any) => void) => {
    controlChangeListenersRef.current.add(listener);
  };

  const unsubscribeFromControlChanges = (listener: (payload: any) => void) => {
    controlChangeListenersRef.current.delete(listener);
  };

  // Pattern subscribe → returns unsubscribe (estilo más idiomático que add/delete por separado).
  // Las páginas lo usan así:
  //   useEffect(() => subscribeToReconnect(() => refetch()), []);
  const subscribeToReconnect = useCallback((listener: () => void) => {
    reconnectListenersRef.current.add(listener);
    return () => { reconnectListenersRef.current.delete(listener); };
  }, []);

  const getUnreadCount = (id: string): number => {
    return unreadCountsRef.current.get(id) || 0;
  };

  return (
    <WebSocketContext.Provider
      value={{
        ...state,
        reconnect,
        disconnect,
        emit,
        removeAlert,
        clearAllAlerts,
        acknowledgeWindowAlert,
        monitorConversation,
        stopMonitoringConversation,
        subscribeToConversationMessages,
        unsubscribeFromConversationMessages,
        subscribeToConversations,
        unsubscribeFromConversations,
        getWindowStatistics,
        subscribeToWindowAlerts,
        unsubscribeFromWindowAlerts,
        notifications: state.alerts,
        markNotificationAsRead,
        subscribeToMetrics,
        removeWindowAlert,
        joinDashboard,
        setCurrentlyViewingConversation,
        markConversationAsRead,
        subscribeToUnreadChanges,
        unsubscribeFromUnreadChanges,
        getUnreadCount,
        subscribeToControlChanges,
        unsubscribeFromControlChanges,
        subscribeToReconnect,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

// Hook para usar el contexto
export const useWebSocket = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocket debe ser usado dentro de un WebSocketProvider');
  }
  return context;
};

export default WebSocketContext;
